// The petrol station's sums: what a nozzle costs per litre and what each "how much?"
// button pumps. Pure — no Phaser, no DOM — so `node --test` can check the arithmetic
// against the server's (backend/app/game/cars.py `refuel`).
//
// The server floors a fixed amount to whole litres, caps it at the room left in the tank
// AND at what you can pay for, then charges `round(litres × price)` whole złoty. We show
// the same whole-złoty totals so the money bar never disagrees with the button we clicked.

export const FIXED_AMOUNTS = [20, 50];

export const NOZZLES = [
  { nozzle: 'pb95', name: 'Pb95', icon: '{dot_green}' },
  { nozzle: 'on', name: 'ON', icon: '{dot_black}' },
];

/** „6,00 zł” — a per-litre price, always two decimals, Polish comma. */
export function zlPerL(price) {
  return `${Number(price ?? 0).toFixed(2).replace('.', ',')} zł/l`;
}

/** „12,5 l” — litres, one decimal at most. */
export function litres(n) {
  const r = Math.round(Number(n ?? 0) * 10) / 10;
  return `${(Number.isInteger(r) ? String(r) : r.toFixed(1)).replace('.', ',')} l`;
}

/** Whole złoty, the way the server bills them. */
export function zl(cost) {
  return `${Math.round(Number(cost ?? 0))} zł`;
}

/** „🟢 Pb95 — 6,00 zł/l”. Deliberately says nothing about which one this car drinks. */
export function nozzleLabel({ icon, name }, price) {
  return `${icon} ${name} — ${zlPerL(price)}`;
}

/**
 * The second step's buttons: fill her up, then the fixed amounts.
 * `{ fuel, capacity }` in litres (kWh for an EV, which never gets here), `price` zł/l,
 * `money` the wallet (default: bottomless, for callers that don't care).
 *
 * A button never promises more than the pump would actually deliver: the server caps
 * every amount at the room left in the tank AND at the whole litres you can pay for,
 * so both caps are shown here — „50 l” must not quietly pump 9, and „Do pełna” must
 * not quote 390 zł to someone holding 40.
 */
export function fuelOptions({ fuel = 0, capacity = 0, price = 0, money = Infinity } = {}) {
  const room = Math.max(0, (capacity ?? 0) - (fuel ?? 0));
  // Whole litres, exactly as the server floors them (`int(money / price)`).
  const affordable = price > 0 ? Math.floor(Math.max(0, money ?? 0) / price) : Infinity;
  const full = Math.min(room, affordable);
  const opts = [];
  if (room <= 0) {
    opts.push({ liters: 'full', amount: 0, cost: 0, disabled: true, label: 'Bak jest pełny {fuel}' });
  } else if (full <= 0) {
    opts.push({ liters: 'full', amount: 0, cost: 0, disabled: true, label: 'Za mało pieniędzy {money_out}' });
  } else {
    opts.push({ liters: 'full', amount: full, cost: full * price, disabled: false,
      label: `Do pełna (${litres(full)} · ${zl(full * price)})` });
  }

  for (const n of FIXED_AMOUNTS) {
    const fits = Math.floor(Math.min(n, room, affordable));
    if (fits >= n) {
      opts.push({ liters: n, amount: n, cost: n * price, disabled: false, label: `${n} l (${zl(n * price)})` });
    } else {
      // Say WHICH wall we hit: an empty wallet and a nearly-full tank are different
      // problems, and only one of them is fixed by driving somewhere else.
      const broke = affordable < room;
      opts.push({ liters: n, amount: fits, cost: fits * price, disabled: true,
        label: broke ? `${n} l — starczy Ci tylko na ${litres(fits)}`
          : `${n} l — zmieści się tylko ${litres(fits)}` });
    }
  }
  return opts;
}
